import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { tracksControllerRecordPlay } from "@/lib/lutely-api/gen/tracks/tracks";
import { AudioPlayerContext } from "./audioPlayerContext";
import type {
  AudioPlayerContextValue,
  LibraryTrack,
  PlayMode,
  PlaylistEntry,
  PlayTrackOptions,
  SetQueueOptions,
} from "./audioPlayerTypes";
import { getSecureRandomIndex } from "./utils";

const STORAGE_KEY = "lutely.audioPlayerState.v1";
const PERSIST_INTERVAL_MS = 1000;
const HISTORY_MAX_LENGTH = 100;

interface PersistedState {
  playMode: PlayMode;
  currentTrackId: string | null;
  currentIndex: number;
  playlist: PlaylistEntry[];
  library?: Record<string, LibraryTrack>;
  currentTimeMs: number;
  durationMs: number;
  volume: number;
}

export default function AudioPlayerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [queue, setQueueState] = useState<PlaylistEntry[]>([]);
  const [playMode, setPlayMode] = useState<PlayMode>("normal");
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [library, setLibrary] = useState<Record<string, LibraryTrack>>({});
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [hydrated, setHydrated] = useState(false);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadedSrcRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const pendingSeekRef = useRef<number | null>(null);
  const autoplayRef = useRef(false);
  const lastPersistRef = useRef(0);
  const persistedStateRef = useRef<PersistedState | null>(null);
  const lastNonZeroVolumeRef = useRef(1);
  const cumulativePlayMsRef = useRef(0);
  const prevPlayTimeMsRef = useRef<number | null>(null);
  const playCountedRef = useRef(false);
  // Mirrors `library` state so that synchronous code (running inside a user-
  // gesture handler) can look up a track's `src` without waiting for React's
  // next render. Android Chrome rejects audio.play() if it isn't called inside
  // the same call stack as the gesture; reading from state would force us to
  // wait for a re-render and lose that window.
  const libraryRef = useRef<Record<string, LibraryTrack>>({});

  const activeTrackId =
    currentIndex >= 0 && currentIndex < queue.length
      ? queue[currentIndex]?.id
      : currentTrackId;

  const currentTrack = activeTrackId ? (library[activeTrackId] ?? null) : null;
  const progress = durationMs > 0 ? Math.min(currentTimeMs / durationMs, 1) : 0;
  const isMuted = volume <= 0.0001;

  const hasNext = useMemo(() => {
    if (!queue.length) return false;
    if (playMode === "repeat-track") return queue.length > 0;
    if (playMode === "repeat-queue") return queue.length > 0;
    if (playMode === "shuffle") return queue.length > 1;
    return currentIndex >= 0 && currentIndex < queue.length - 1;
  }, [queue, currentIndex, playMode]);

  const hasPrevious = useMemo(() => {
    if (!queue.length) return false;
    if (playMode === "repeat-track") return queue.length > 0;
    if (playMode === "repeat-queue") return queue.length > 0;
    if (playMode === "shuffle") return queue.length > 1;
    return currentIndex > 0;
  }, [queue, currentIndex, playMode]);

  // Hydrate persisted queue and playback state on first mount.
  useEffect(() => {
    if (hasHydratedRef.current) {
      return;
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed: PersistedState = JSON.parse(raw);
        if (Array.isArray(parsed.playlist) && parsed.playlist.length > 0) {
          const nextQueue = parsed.playlist.map((entry) => ({
            id: entry.id,
            playedCount:
              typeof entry.playedCount === "number" ? entry.playedCount : 0,
          }));
          setQueueState(nextQueue);
          const hydratedIndex =
            typeof parsed.currentIndex === "number" &&
            parsed.currentIndex >= 0 &&
            parsed.currentIndex < nextQueue.length
              ? parsed.currentIndex
              : parsed.currentTrackId
                ? Math.max(
                    nextQueue.findIndex(
                      (entry) => entry.id === parsed.currentTrackId,
                    ),
                    0,
                  )
                : Math.max(nextQueue.length - 1, 0);

          setCurrentIndex(hydratedIndex);
          setCurrentTrackId(nextQueue[hydratedIndex]?.id ?? null);
          setRestoredFromStorage(true);
        }
        if (
          typeof parsed.currentTimeMs === "number" &&
          parsed.currentTimeMs >= 0
        ) {
          pendingSeekRef.current = parsed.currentTimeMs;
          setCurrentTimeMs(parsed.currentTimeMs);
        }
        if (typeof parsed.durationMs === "number" && parsed.durationMs > 0) {
          setDurationMs(parsed.durationMs);
        }
        if (parsed.playMode) {
          setPlayMode(parsed.playMode);
        }
        if (
          typeof parsed.volume === "number" &&
          parsed.volume >= 0 &&
          parsed.volume <= 1
        ) {
          setVolumeState(parsed.volume);
          if (parsed.volume > 0) {
            lastNonZeroVolumeRef.current = parsed.volume;
          }
        }
        if (parsed.library && typeof parsed.library === "object") {
          libraryRef.current = { ...parsed.library };
          setLibrary(parsed.library);
        }
      }
    } catch (error) {
      console.warn("audio-player: failed to load state", error);
    }
    hasHydratedRef.current = true;
    setHydrated(true);
  }, []);

  const persistedState = useMemo<PersistedState>(
    () => ({
      playlist: queue,
      playMode,
      currentTrackId: activeTrackId ?? currentTrackId,
      currentIndex,
      library,
      currentTimeMs,
      durationMs,
      volume,
    }),
    [
      queue,
      playMode,
      activeTrackId,
      currentTrackId,
      currentIndex,
      currentTimeMs,
      durationMs,
      library,
      volume,
    ],
  );

  const persistState = useCallback((state: PersistedState) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn("audio-player: failed to persist state", error);
    }
  }, []);

  // Persist the latest player snapshot on an interval so we avoid thrashing storage.
  useEffect(() => {
    persistedStateRef.current = persistedState;
  }, [persistedState]);

  // Keep the audio element volume aligned with provider state.
  useEffect(() => {
    if (!hasHydratedRef.current) return;
    const now = Date.now();
    if (now - lastPersistRef.current < PERSIST_INTERVAL_MS) return;
    lastPersistRef.current = now;
    persistState(persistedState);
  }, [persistState, persistedState]);

  // Mirror native volumechange events back into React state.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleBeforeUnload = () => {
      const latest = persistedStateRef.current;
      if (!latest) return;
      persistState(latest);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [persistState]);

  // Generate waveform data for the active track when peaks are missing.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const clamped = Math.min(Math.max(volume, 0), 1);
    if (Math.abs(audio.volume - clamped) > 0.001) {
      audio.volume = clamped;
    }
  }, [volume]);

  // Attach audio element listeners for metadata, progress, play/pause, and ended.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleVolumeChange = () => {
      const next = Math.min(Math.max(audio.volume, 0), 1);
      setVolumeState((prev) => {
        if (Math.abs(prev - next) <= 0.001) {
          return prev;
        }
        if (next > 0) {
          lastNonZeroVolumeRef.current = next;
        }
        return next;
      });
    };

    audio.addEventListener("volumechange", handleVolumeChange);
    return () => {
      audio.removeEventListener("volumechange", handleVolumeChange);
    };
  }, []);

  const registerTracks = useCallback((tracks: LibraryTrack[]) => {
    if (!tracks.length) return;
    // Update ref synchronously so an immediately-following setQueue/playTrack
    // call (typical pattern in click handlers) can look up the src now.
    for (const track of tracks) {
      libraryRef.current[track.id] = track;
    }
    setLibrary((prev) => {
      const next = { ...prev };
      for (const track of tracks) {
        next[track.id] = track;
      }
      return next;
    });
  }, []);

  // Start playback synchronously inside the same call stack as the user's
  // click/tap. On Android Chrome (and other mobile browsers with strict
  // autoplay policies) audio.play() is only allowed when the user gesture is
  // still "active" — by the time React re-renders and our useEffect picks up
  // the new currentTrack, the gesture has been spent and play() rejects.
  // Falls back to the loadedmetadata autoplay path if we can't resolve src now.
  const playImmediately = useCallback((trackId: string) => {
    const audio = audioRef.current;
    const track = libraryRef.current[trackId];
    if (!audio || !track?.src) return;
    if (loadedSrcRef.current !== track.src) {
      audio.src = track.src;
      audio.load();
      loadedSrcRef.current = track.src;
    }
    audio.play().catch((error) => {
      console.warn("audio-player: sync play failed", error);
    });
  }, []);

  const clearQueue = useCallback(() => {
    setQueueState([]);
    setCurrentTrackId(null);
    setCurrentIndex(-1);
    setCurrentTimeMs(0);
    setDurationMs(0);
    setRestoredFromStorage(false);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
    }
    loadedSrcRef.current = null;
  }, []);

  const updatePlayedCount = useCallback((trackId: string | null) => {
    if (!trackId) return;
    setQueueState((prev) =>
      prev.map((entry) =>
        entry.id === trackId
          ? {
              id: entry.id,
              playedCount: entry.playedCount + 1,
            }
          : entry,
      ),
    );
  }, []);

  const seekInternal = useCallback((ms: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(ms)) {
      return;
    }
    pendingSeekRef.current = ms;
    if (audio.readyState >= 1) {
      audio.currentTime = ms / 1000;
      pendingSeekRef.current = null;
    }
    setCurrentTimeMs(ms);
  }, []);

  const setVolume = useCallback((value: number) => {
    const clamped = Math.min(Math.max(value, 0), 1);
    if (clamped > 0) {
      lastNonZeroVolumeRef.current = clamped;
    }
    setVolumeState(clamped);
  }, []);

  const toggleMute = useCallback(() => {
    setVolumeState((prev) => {
      if (prev === 0) {
        const restored =
          lastNonZeroVolumeRef.current > 0 ? lastNonZeroVolumeRef.current : 1;
        return restored;
      }
      if (prev > 0) {
        lastNonZeroVolumeRef.current = prev;
      }
      return 0;
    });
  }, []);

  const setQueue = useCallback(
    ({
      trackIds,
      startTrackId,
      autoPlay,
      startPositionMs,
      replace = false,
    }: SetQueueOptions) => {
      setRestoredFromStorage(false);
      setQueueState((prev) => {
        const base = replace ? [] : [...prev];
        let nextQueue = [...base];

        for (const id of trackIds) {
          if (!id) continue;
          const lastId = nextQueue[nextQueue.length - 1]?.id;
          if (lastId === id) continue; // collapse adjacent duplicates
          nextQueue.push({ id, playedCount: 0 });
        }

        if (nextQueue.length > HISTORY_MAX_LENGTH) {
          const overflow = nextQueue.length - HISTORY_MAX_LENGTH;
          nextQueue = nextQueue.slice(overflow);
        }

        const fallbackId =
          startTrackId ??
          trackIds[0] ??
          (nextQueue.length > 0
            ? nextQueue[nextQueue.length - 1]?.id
            : undefined);

        let nextIndex = -1;
        for (let i = nextQueue.length - 1; i >= 0; i -= 1) {
          if (nextQueue[i]?.id === fallbackId) {
            nextIndex = i;
            break;
          }
        }
        if (nextIndex === -1 && nextQueue.length > 0) {
          nextIndex = nextQueue.length - 1;
        }

        setCurrentIndex(nextIndex);
        setCurrentTrackId(
          nextIndex >= 0 ? (nextQueue[nextIndex]?.id ?? null) : null,
        );

        return nextQueue;
      });

      const seekMs = startPositionMs ?? 0;
      pendingSeekRef.current = seekMs;
      setCurrentTimeMs(seekMs);
      if (autoPlay) {
        autoplayRef.current = true;
        const playId = startTrackId ?? trackIds[0];
        if (playId) playImmediately(playId);
      }
    },
    [playImmediately],
  );

  const playTrack = useCallback(
    (trackId: string, options?: PlayTrackOptions) => {
      if (!trackId) return;
      setRestoredFromStorage(false);
      setQueueState((prev) => {
        let nextQueue = [...prev];
        const lastId = nextQueue[nextQueue.length - 1]?.id;
        const targetExistsIndex = (() => {
          for (let i = nextQueue.length - 1; i >= 0; i -= 1) {
            if (nextQueue[i]?.id === trackId) return i;
          }
          return -1;
        })();

        const shouldAppend = options?.appendToHistory ?? true;

        if (!shouldAppend && targetExistsIndex >= 0) {
          setCurrentIndex(targetExistsIndex);
          setCurrentTrackId(nextQueue[targetExistsIndex]?.id ?? trackId);
          return nextQueue;
        }

        if (lastId !== trackId) {
          nextQueue.push({ id: trackId, playedCount: 0 });
        }

        if (nextQueue.length > HISTORY_MAX_LENGTH) {
          const overflow = nextQueue.length - HISTORY_MAX_LENGTH;
          nextQueue = nextQueue.slice(overflow);
        }

        const nextIndex = nextQueue.length - 1;
        setCurrentIndex(nextIndex);
        setCurrentTrackId(nextQueue[nextIndex]?.id ?? trackId);
        return nextQueue;
      });

      const seekMs = options?.startPositionMs ?? 0;
      pendingSeekRef.current = seekMs;
      setCurrentTimeMs(seekMs);
      if (options?.autoPlay) {
        autoplayRef.current = true;
        playImmediately(trackId);
      }
    },
    [playImmediately],
  );

  const getNextIndex = useCallback(
    (direction: 1 | -1): number | null => {
      if (!queue.length) return null;

      let index = currentIndex;
      if (index === -1) {
        index = queue.length - 1;
      }

      if (playMode === "repeat-track") {
        return index;
      }

      if (playMode === "shuffle") {
        if (queue.length === 1) return index;
        const others = queue.filter((_, idx) => idx !== index);
        if (!others.length) return index;
        const randomIndex = getSecureRandomIndex(others.length);
        const chosen = others[randomIndex];
        const chosenIndex = queue.findIndex((entry) => entry.id === chosen.id);
        return chosenIndex >= 0 ? chosenIndex : index;
      }

      const nextIndex = index + direction;
      if (nextIndex < 0) {
        return playMode === "repeat-queue" ? queue.length - 1 : null;
      }
      if (nextIndex >= queue.length) {
        if (playMode === "repeat-queue") {
          return 0;
        }
        return null;
      }
      return nextIndex;
    },
    [queue, currentIndex, playMode],
  );

  const playNext = useCallback(() => {
    const nextIndex = getNextIndex(1);
    if (nextIndex == null) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
      setIsPlaying(false);
      return;
    }
    const nextId = queue[nextIndex]?.id;
    if (!nextId) return;
    playTrack(nextId, {
      autoPlay: true,
      startPositionMs: 0,
      appendToHistory: false,
    });
  }, [getNextIndex, playTrack, queue]);

  const playPrevious = useCallback(() => {
    const previousIndex = getNextIndex(-1);
    if (previousIndex == null) return;
    const previousId = queue[previousIndex]?.id;
    if (!previousId) return;
    playTrack(previousId, {
      autoPlay: true,
      startPositionMs: 0,
      appendToHistory: false,
    });
  }, [getNextIndex, playTrack, queue]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!currentTrackId && queue.length === 0) {
      return;
    }

    if (!currentTrackId && queue.length > 0) {
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const startId = queue[startIndex]?.id ?? queue[0]?.id;
      if (startId) {
        setCurrentIndex(startIndex);
        playTrack(startId, {
          autoPlay: true,
          startPositionMs: 0,
          appendToHistory: false,
        });
      }
      return;
    }

    if (audio.paused) {
      audio.play().catch((error) => {
        console.warn("audio-player: play failed", error);
      });
    } else {
      audio.pause();
    }
  }, [currentTrackId, currentIndex, playTrack, queue]);

  const seekTo = useCallback(
    (targetProgress: number) => {
      if (!Number.isFinite(targetProgress)) return;
      const nextProgress = Math.min(Math.max(targetProgress, 0), 1);

      if (!durationMs || durationMs <= 0) return;

      // Cap seeking to 30 seconds for preview limit
      const targetMs = nextProgress * durationMs;

      // const maxPreviewMs = 30000;
      // const cappedMs = Math.min(targetMs, maxPreviewMs);

      seekInternal(targetMs);
    },
    [durationMs, seekInternal],
  );

  // Persist final state before the page unloads.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      const ms = Number.isFinite(audio.duration) ? audio.duration * 1000 : 0;
      setDurationMs(ms);
      setIsReady(true);
      const pending = pendingSeekRef.current;
      if (pending != null && pending >= 0 && Number.isFinite(pending)) {
        if (ms > 0) {
          audio.currentTime = Math.min(ms, pending) / 1000;
        }
        setCurrentTimeMs(pending);
        pendingSeekRef.current = null;
      }
      if (autoplayRef.current) {
        autoplayRef.current = false;
        audio.play().catch((error) => {
          console.warn("audio-player: autoplay failed", error);
        });
      }
    };

    const handleTimeUpdate = () => {
      const currentSeconds = audio.currentTime;
      setCurrentTimeMs(currentSeconds * 1000);

      // Hard stop at 30 seconds for all tracks
      // if (currentSeconds >= 30) {
      //   audio.pause();
      // }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      updatePlayedCount(activeTrackId);
      playNext();
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [activeTrackId, playNext, updatePlayedCount]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!currentTrack) {
      audio.pause();
      audio.removeAttribute("src");
      loadedSrcRef.current = null;
      setIsReady(false);
      setDurationMs(0);
      setCurrentTimeMs(0);
      return;
    }

    if (loadedSrcRef.current !== currentTrack.src) {
      setIsReady(false);
      if (!restoredFromStorage) {
        setDurationMs(0);
      }
      audio.src = currentTrack.src;
      audio.load();
      loadedSrcRef.current = currentTrack.src;
    }
  }, [currentTrack, restoredFromStorage]);

  // Reset play tracking when the active track changes.
  useEffect(() => {
    cumulativePlayMsRef.current = 0;
    prevPlayTimeMsRef.current = null;
    playCountedRef.current = false;
  }, [activeTrackId]);

  // Accumulate actual play time from timeupdate deltas; count once per 30s of
  // genuine listening. Resets on track restart (time drops back near zero after
  // meaningful play, e.g. natural end + loop or seeking back to beginning).
  useEffect(() => {
    if (currentTimeMs < 1_000 && cumulativePlayMsRef.current > 5_000) {
      cumulativePlayMsRef.current = 0;
      prevPlayTimeMsRef.current = null;
      playCountedRef.current = false;
    }

    if (!isPlaying) {
      prevPlayTimeMsRef.current = null;
      return;
    }

    const prev = prevPlayTimeMsRef.current;
    prevPlayTimeMsRef.current = currentTimeMs;

    if (prev !== null) {
      const delta = currentTimeMs - prev;
      // Ignore seeks (large jumps) and negative deltas
      if (delta > 0 && delta < 2_000) {
        cumulativePlayMsRef.current += delta;
      }
    }

    if (!activeTrackId || playCountedRef.current) return;
    const playThresholdMs =
      durationMs > 0 && durationMs < 30_000 ? durationMs * 0.9 : 30_000;
    if (cumulativePlayMsRef.current >= playThresholdMs) {
      playCountedRef.current = true;
      tracksControllerRecordPlay(activeTrackId).catch(() => {});
    }
  }, [activeTrackId, currentTimeMs, durationMs, isPlaying]);

  const contextValue = useMemo<AudioPlayerContextValue>(
    () => ({
      queue,
      playMode,
      currentTrackId,
      currentTrack,
      currentTimeMs,
      durationMs,
      progress,
      isPlaying,
      isReady,
      volume,
      isMuted,
      hasNext,
      hasPrevious,
      hydrated,
      restoredFromStorage,
      registerTracks,
      setQueue,
      clearQueue,
      playTrack,
      playNext,
      playPrevious,
      togglePlay,
      pause,
      seekTo,
      setPlayMode,
      setVolume,
      toggleMute,
    }),
    [
      queue,
      playMode,
      currentTrackId,
      currentTrack,
      currentTimeMs,
      durationMs,
      progress,
      isPlaying,
      isReady,
      volume,
      isMuted,
      hasNext,
      hasPrevious,
      hydrated,
      restoredFromStorage,
      registerTracks,
      setQueue,
      clearQueue,
      playTrack,
      playNext,
      playPrevious,
      togglePlay,
      pause,
      seekTo,
      setPlayMode,
      setVolume,
      toggleMute,
    ],
  );

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      <audio ref={audioRef} preload="auto" className="hidden" />
    </AudioPlayerContext.Provider>
  );
}
